// ============================================================
// useSessionCharacterLibrary - the session's characters as the character
// library the panel reads and writes (UseCharacterLibraryReturn).
//
// There was a second, localStorage-backed one for play outside a session;
// the app has no play outside a session, so it never ran, and it went with
// the old stores (assets stage 4). What an older version kept in a browser
// is offered to the account's asset library instead (utils/browserCharacters).
// ============================================================

import { useState, useCallback, useMemo } from 'react';
import type { Character } from '../engine/character/types';
import type { SharedCharacterEntry } from './useCharacterSync';
import deepClone from '../utils/deepClone';
import { normalizeCharacter } from '../engine/character/normalize';
import { randomToken } from '../utils/entropy';
import { withImportableCharacterIds } from '../engine/importIds';

/** The character library the panel reads and writes. */
export interface UseCharacterLibraryReturn {
  characters: Character[];
  loading: boolean;
  activeTag: string | null;
  allTags: string[];
  recentIds: string[];
  saveCharacter: (character: Character) => void;
  deleteCharacter: (id: string) => void;
  duplicateCharacter: (id: string) => Character | null;
  importCharacters: (json: string) => Character[];
  exportCharacters: (ids: string[]) => string;
  filterByTag: (tag: string | null) => void;
  addRecentLoad: (id: string) => void;
}

interface Params {
  sessionCharacters: SharedCharacterEntry[];
  synced: boolean;
  pushCharacter: (character: Character) => void;
  pushAllCharacters: (characters: Character[]) => void;
  deleteSessionCharacter: (id: string) => void;
}

/** A file's entries: a bare array, the local library's `{ version, characters }`, or one object. */
function entriesOf(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  const wrapped = (data as { characters?: unknown[] })?.characters;
  return Array.isArray(wrapped) ? wrapped : [data];
}

const isNamedCharacter = (c: unknown): c is Character => {
  const o = c as Partial<Character> | null;
  return !!o && typeof o === 'object' && typeof o.name === 'string' && o.name.trim().length > 0;
};

/**
 * READ WHAT THE OTHER LIBRARY WRITES, and refuse what is not a character.
 *
 * There are two libraries - this one (shared, in the session) and the local
 * one in `utils/characterStorage` - and they did not agree on a file format.
 * The local library EXPORTS `{ version, characters: [...] }`; this one used
 * to accept only a bare array, so `Array.isArray(data)` was false for that
 * file and the whole WRAPPER was pushed as a single character. It has no
 * name and no sheet, so it arrived as "New Character" - or, once written
 * and read back, as a nameless row with default stats. That is the imported
 * -character bug, and it depended only on which way round you moved a file.
 *
 * So: accept both shapes, normalise (the local importer always did, this one
 * never did), and DROP entries that are not characters instead of importing
 * a placeholder. A file that yields nothing is better than a roster full of
 * things called "New Character" that have to be found and deleted by hand.
 * Ids are checked at the same door (`importIds`): each becomes
 * `characters/{id}`, and a "/" in one split that path.
 */
function charactersInFile(json: string): Character[] {
  return entriesOf(withImportableCharacterIds(JSON.parse(json)))
    .filter(isNamedCharacter)
    .map((c) => normalizeCharacter({ ...deepClone(c), id: c.id || newCharacterId() }));
}

/**
 * An id for a character that has none. Each its own: an entry with no id
 * used to become `char-normalized`, so two of them were one document.
 */
const newCharacterId = () => 'char-' + Date.now() + '-' + randomToken(4);

/** A copy under a new id, named as one. */
function copyOf(orig: Character): Character {
  const clone = deepClone(orig);
  clone.id = newCharacterId();
  clone.name = orig.name + ' (Copy)';
  return clone;
}

export default function useSessionCharacterLibrary(params: Params): UseCharacterLibraryReturn {
  const { sessionCharacters, synced } = params;
  const allCharacters = useMemo(() => sessionCharacters.map((e) => e.character), [sessionCharacters]);
  const { characters, activeTag, allTags, filterByTag } = useTagFilter(allCharacters);
  const { recentIds, addRecentLoad } = useRecentLoads();
  const writes = useSessionWrites(params, allCharacters);
  const exportCharacters = useCallback(
    (ids: string[]): string => {
      const toExport = ids.length > 0 ? allCharacters.filter((c) => ids.includes(c.id)) : allCharacters;
      return JSON.stringify(toExport, null, 2);
    },
    [allCharacters],
  );
  return {
    characters,
    loading: !synced,
    activeTag,
    allTags,
    recentIds,
    ...writes,
    exportCharacters,
    filterByTag,
    addRecentLoad,
  };
}

/** The tag filter over the whole list, and every tag in it (from the unfiltered list). */
function useTagFilter(allCharacters: Character[]) {
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const characters = useMemo(
    () => (activeTag ? allCharacters.filter((c) => c.tags?.includes(activeTag)) : allCharacters),
    [allCharacters, activeTag],
  );
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    allCharacters.forEach((c) => c.tags?.forEach((t) => tags.add(t)));
    return [...tags].sort();
  }, [allCharacters]);
  const filterByTag = useCallback((tag: string | null) => {
    setActiveTag(tag);
  }, []);
  return { characters, activeTag, allTags, filterByTag };
}

/** The five most recently loaded, newest first. */
function useRecentLoads() {
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const addRecentLoad = useCallback((id: string) => {
    setRecentIds((prev) => [id, ...prev.filter((x) => x !== id)].slice(0, 5));
  }, []);
  return { recentIds, addRecentLoad };
}

/** Save, delete, duplicate and import - all straight through to the session. */
function useSessionWrites(
  { pushCharacter, pushAllCharacters, deleteSessionCharacter }: Params,
  allCharacters: Character[],
) {
  const saveCharacter = useCallback(
    (character: Character) => {
      pushCharacter(character);
    },
    [pushCharacter],
  );
  const deleteCharacter = useCallback(
    (id: string) => {
      deleteSessionCharacter(id);
    },
    [deleteSessionCharacter],
  );
  const duplicateCharacter = useDuplicate(allCharacters, pushCharacter);
  const importCharacters = useCallback(
    (json: string): Character[] => {
      const chars = charactersInFile(json);
      if (chars.length > 0) pushAllCharacters(chars);
      return chars;
    },
    [pushAllCharacters],
  );
  return { saveCharacter, deleteCharacter, duplicateCharacter, importCharacters };
}

/** Duplicate one character into the session; the copy, or null when it is not there. */
function useDuplicate(allCharacters: Character[], pushCharacter: Params['pushCharacter']) {
  return useCallback(
    (id: string): Character | null => {
      const orig = allCharacters.find((c) => c.id === id);
      if (!orig) return null;
      const clone = copyOf(orig);
      pushCharacter(clone);
      return clone;
    },
    [allCharacters, pushCharacter],
  );
}
